import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import { Close } from '@mui/icons-material';
import {
  Box,
  Button,
  Drawer,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useState } from 'react';
import { useQueryClient } from 'react-query';
import { useHistory } from 'react-router-dom';
import { handleErrorToast } from 'src/components/molecules';
import {
  useCreateCategoryMutation,
  useGetCategoryConfigCategoriesQuery,
} from 'src/graphql/generated';
import { DiscardChangesModal } from './DiscardChangesModal';

const MAX_NAME_LENGTH = 140;
const DEFAULT_LOCALE = 'en-GB';

interface CreateCoiCategoryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const CreateCoiCategoryDrawer: React.FC<
  CreateCoiCategoryDrawerProps
> = ({ isOpen, onClose, onSuccess }) => {
  const { tokens } = useTheme<LensTheme>();
  const history = useHistory();
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState(false);

  const sp = tokens.semantic.spacing.desktop.gap;
  const color = tokens.semantic.color;

  const queryClient = useQueryClient();
  const { data } = useGetCategoryConfigCategoriesQuery({ isCOI: true });
  const categories = data?.getOrganisationCategories ?? [];

  const { mutateAsync: createCategory } = useCreateCategoryMutation();

  const isDuplicateName = (newName: string): boolean => {
    if (!newName.trim()) return false;
    return categories.some(
      (cat) => cat.locales?.[DEFAULT_LOCALE]?.name === newName.trim(),
    );
  };

  const handleNameChange = (value: string) => {
    const trimmed = value.slice(0, MAX_NAME_LENGTH);
    setName(trimmed);
    if (nameError && !isDuplicateName(trimmed)) {
      setNameError('');
    }
  };

  const handleSave = async () => {
    if (isSaving) return;
    if (name.trim() && isDuplicateName(name.trim())) {
      setNameError(
        'Category name already exists. Please enter a unique name or modify the existing category.',
      );
      return;
    }

    try {
      setIsSaving(true);
      const maxOrder = categories.reduce(
        (max, cat) => Math.max(max, cat.order),
        -1,
      );
      const order = maxOrder + 1;

      await createCategory({
        category: {
          isDeterminedCategory: false,
          isReportedCategory: true,
          order,
          question: false,
          allowGoTogether: false,
          // Required for backend status computation to produce form_required
          displayInApp: true,
          displayInManualIntake: false,
          displayInOpenReporting: true,
          displayInVaultTalk: false,
          displayInCollaborationToolIntegration: false,
          requestOpenPerpetrators: false,
          locales: {
            [DEFAULT_LOCALE]: { name: name.trim(), description: '' },
          },
          isCOI: true,
          isCustomQuestions: true,
        },
      });

      queryClient.refetchQueries(['GetCategoryConfigCategories']);
      handleClose();
      onSuccess?.();
    } catch {
      handleErrorToast({ toastMessage: 'Error creating category' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setName('');
    setNameError('');
    setIsDiscardModalOpen(false);
    onClose();
  };

  const hasUnsavedChanges = name.trim().length > 0;

  const handleRequestClose = () => {
    if (hasUnsavedChanges) {
      setIsDiscardModalOpen(true);
    } else {
      handleClose();
    }
  };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={handleRequestClose}
      ModalProps={{ keepMounted: false }}
      data-testid="create-coi-category-drawer"
      sx={{
        '& .MuiDrawer-paper': {
          width: '567px',
          padding: sp[3].value,
          backgroundColor: color.surface.default.value,
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '100vh',
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
        }}
      >
        <Typography
          component="h2"
          sx={{
            fontSize: '24px',
            fontWeight: tokens.semantic.fontWeight.emphasis.value,
            color: color.type.default.value,
          }}
        >
          Create new category
        </Typography>
        <IconButton
          onClick={handleRequestClose}
          aria-label="close"
          data-testid="create-coi-category-drawer-close"
          sx={{
            color: color.action.secondary.onSecondary.value,
            '&:hover': {
              backgroundColor: color.action.secondary.hoverFill.value,
            },
          }}
        >
          <Close />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          padding: sp[1].value,
        }}
      >
        <Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              mb: '4px',
            }}
          >
            <Typography
              component="label"
              sx={{
                fontSize: '14px',
                fontWeight: tokens.semantic.fontWeight.emphasis.value,
                color: color.type.default.value,
              }}
            >
              Category name{' '}
              <span style={{ color: color.type.muted.value }}>*</span>
            </Typography>
            <Typography
              component="span"
              sx={{ fontSize: '12px', color: color.type.muted.value }}
            >
              {name.length}/{MAX_NAME_LENGTH}
            </Typography>
          </Box>
          <TextField
            fullWidth
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            placeholder="Enter category name"
            data-testid="create-coi-category-name-input"
            inputProps={{ maxLength: MAX_NAME_LENGTH }}
            error={!!nameError}
          />
          {nameError && (
            <Typography
              sx={{
                fontSize: '12px',
                color: color.status.error.default.value,
                mt: '4px',
              }}
              data-testid="create-coi-category-name-error"
            >
              {nameError}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            padding: '8px 16px',
            borderRadius: '12px',
            backgroundColor: color.status.notification.background.value,
          }}
          data-testid="create-coi-category-form-required-alert"
        >
          <Box sx={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
            <Box
              component="i"
              className="uil uil-info-circle"
              sx={{
                fontSize: '24px',
                color: color.status.notification.text.value,
                flexShrink: 0,
              }}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <Typography
                sx={{
                  fontSize: '16px',
                  fontWeight: tokens.semantic.fontWeight.emphasis.value,
                  color: color.status.notification.text.value,
                }}
              >
                Form required
              </Typography>
              <Typography
                sx={{
                  fontSize: '16px',
                  color: color.status.notification.text.value,
                }}
              >
                Conflict of interest categories require a form before they can
                be set live. You can save your changes, but the category will
                only go live once a form is created.
              </Typography>
            </Box>
          </Box>
          <Button
            variant="text"
            onClick={() => {
              handleClose();
              history.push('/form-management');
            }}
            endIcon={<i className="uil uil-arrow-right" />}
            data-testid="create-coi-category-go-to-form-creation"
            sx={{
              textTransform: 'none',
              fontWeight: tokens.semantic.fontWeight.emphasis.value,
              color: color.status.notification.text.value,
              padding: '8px 12px',
              alignSelf: 'flex-start',
              borderRadius: '12px',
              '&:hover': { backgroundColor: 'transparent' },
            }}
          >
            Go to Form creation
          </Button>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            padding: '16px',
            borderRadius: '12px',
            border: `1px solid ${color.ui.divider.default.value}`,
          }}
          data-testid="create-coi-category-intake-channel"
        >
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: tokens.semantic.fontWeight.emphasis.value,
              color: color.type.default.value,
            }}
          >
            Intake channel
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              color: color.type.muted.value,
            }}
          >
            Conflict of interest disclosures can only be submitted via Vault
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pt: sp[3].value,
          flexShrink: 0,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleRequestClose}
          data-testid="create-coi-category-cancel-button"
          sx={{
            textTransform: 'none',
            minWidth: '88px',
            height: '40px',
            borderRadius: tokens.semantic.radius.lg.value,
            borderColor: color.type.default.value,
            color: color.type.default.value,
            '&:hover': {
              borderColor: color.type.default.value,
              backgroundColor: 'transparent',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!name.trim() || isSaving}
          data-testid="create-coi-category-save-button"
          disableElevation
          sx={{
            textTransform: 'none',
            minWidth: '88px',
            height: '40px',
            borderRadius: tokens.semantic.radius.lg.value,
            backgroundColor: color.type.default.value,
            color: color.surface.default.value,
            '&:hover': {
              backgroundColor: color.type.default.value,
              opacity: 0.9,
            },
            '&.Mui-disabled': {
              backgroundColor: color.ui.divider.default.value,
              color: color.type.muted.value,
            },
          }}
        >
          Save
        </Button>
      </Box>

      <DiscardChangesModal
        open={isDiscardModalOpen}
        onClose={() => setIsDiscardModalOpen(false)}
        onConfirm={handleClose}
      />
    </Drawer>
  );
};
